import { Search } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  CircularProgress,
  Fade,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Popper,
  TextField,
} from '@mui/material';
import DeleteIcon from '@src/assets/icons/delete.svg';
import React, { useState } from 'react';
import apis from '@src/apis';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import VirtualList from 'rc-virtual-list';
import NoData from '@src/components/NoData';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import Popconfirm from '@src/components/Popconfirm';
// import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { StyledTableCell } from '@src/components/NewTable/index.style';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import Tooltip from '@src/components/Tooltip';
import { v4 as uuid } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import actions from '@src/redux/actions';
import { HEIGHT_ITEM, HEIGHT_LIST } from '@src/constants/dictionary';
import { StyledIconButton } from '../TTSNew/Requests/index.style';
import { StyledInput, StyledActionConfirm } from './index.style';
import { checkValidatePronunciation, validateValueInputWord } from './utils';

const InputDictionary = ({
  value,
  type,
  placeholder,
  wordsView,
  wordsData,
  index,
  fetchDictionary,
  onWordRowChange,
}) => {
  const [valueInput, setValueInput] = useState(value);
  const [isDirty, setIsDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState({
    word: '',
    pronunciation: '',
  });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const disabledUpdate = Object.values(errorMsg)?.some(Boolean);
  // eslint-disable-next-line no-shadow
  const checkValidateForm = (value, key) => {
    if (key === 'pronunciation') {
      const [invalid, empty, overMaxLength] = checkValidatePronunciation(value);
      if (empty) {
        setErrorMsg((prev) => ({
          ...prev,
          [key]: t('invalidEmptyPronunciation'),
        }));
        return;
      }
      if (invalid) {
        setErrorMsg((prev) => ({
          ...prev,
          [key]: t('invalidPronunciationOfNewDictionary'),
        }));
        return;
      }
      if (overMaxLength) {
        setErrorMsg((prev) => ({
          ...prev,
          [key]: t('overMaxLengthPronunciation'),
        }));
        return;
      }
      setErrorMsg((prev) => ({
        ...prev,
        [key]: '',
      }));
      return;
    }
    const [trim, empty, duplicate, invalid] = validateValueInputWord(
      wordsView,
      value,
    );
    if (empty) {
      setErrorMsg((prev) => ({
        ...prev,
        [key]: t('emptyTextDictionary'),
      }));
      return;
    }
    if (duplicate) {
      setErrorMsg((prev) => ({
        ...prev,
        [key]: t('duplicateTextDictionary'),
      }));
      return;
    }
    if (trim) {
      setErrorMsg((prev) => ({
        ...prev,
        [key]: t('maxCharTextDictionary'),
      }));
      return;
    }
    if (invalid) {
      setErrorMsg((prev) => ({
        ...prev,
        [key]: t('invalidWordOfNewDictionary'),
      }));
      return;
    }

    setErrorMsg((prev) => ({
      ...prev,
      [key]: '',
    }));
  };
  const handleOnChange = (e) => {
    setValueInput(e.target.value);
    setIsDirty(true);
    checkValidateForm(e.target.value, type);
  };

  let preventBlur = false;

  const handleOnBlur = () => {
    setTimeout(() => {
      if (preventBlur) return;
      setIsDirty(false);
      setValueInput(value);
      setErrorMsg({
        word: '',
        pronunciation: '',
      });
    }, 500);
  };

  const onUpdateDictionary = async () => {
    if (Object.values(errorMsg)?.some(Boolean)) return;
    setLoading(true);
    onWordRowChange();
    preventBlur = true;
    const tempArray = [...wordsData];
    const selectItemChanged = tempArray?.find(
      (item) => item[type] === wordsView[index][type],
    );
    selectItemChanged[type] = valueInput;
    const data = await apis.voices.createDictionary(tempArray);
    if (data?.status) {
      await fetchDictionary(true);
    } else {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'updateDictionaryFailure',
        }),
      );
    }
    setLoading(false);
    preventBlur = false;
    handleOnBlur();
  };

  const renderIconCheckAndClose = () => (
    <>
      {loading ? (
        <CircularProgress
          sx={{
            width: '28px !important',
            height: '28px !important',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '4px',
            color: '#6F6A8E',
          }}
        />
      ) : (
        <StyledIconButton
          height="24px"
          hiddenBorder
          width="24px"
          borderRadius="100px"
          isDisableHover
          padding="12px"
          onClick={onUpdateDictionary}
          disabled={disabledUpdate}
          data-id="confirm-edit-pronunciation"
        >
          <CheckRoundedIcon
            sx={{ color: '#34C759 !important' }}
            className="icon"
          />
        </StyledIconButton>
      )}
      <StyledIconButton
        disabled={false}
        height="24px"
        hiddenBorder
        width="24px"
        borderRadius="100px"
        isDisableHover
        padding="12px"
        data-id="cancel-edit-pronunciation"
      >
        <CloseRoundedIcon className="icon" />
      </StyledIconButton>
    </>
  );
  const emptyBox = () => (
    <StyledIconButton
      disabled
      height="40px"
      hiddenBorder
      width="40px"
      borderRadius="8px"
      isDisableHover
      padding="12px"
    />
  );
  return (
    <StyledInput isError={!!errorMsg[type]}>
      <div>
        <div className={`input ${type}`} data-id="edit-pronunciation-dict">
          <input
            onBlur={handleOnBlur}
            onChange={handleOnChange}
            value={valueInput}
            name={`${uuid()}-${type}`}
            placeholder={placeholder}
            style={{ height: '24px' }}
          />
          {isDirty ? renderIconCheckAndClose() : emptyBox()}
        </div>
      </div>
      <div className="msg_error">{errorMsg[type]}</div>
    </StyledInput>
  );
};

const FilterDictionary = ({
  optionsFilter,
  sort,
  onSort,
  onSearch,
  searchText,
}) => {
  const { t } = useTranslation();
  const [anchorStatusRequest, setAnchorStatusRequest] = useState(null);
  const [openPopperStatusRequest, setOpenPopperStatusRequest] = useState(false);
  const refClickOutSide = useClickOutside(() => {
    setOpenPopperStatusRequest(false);
  });
  return (
    <div className="filter_container">
      <div className="title_list_dictionary">{t('listDictionary')} </div>
      <div className="filter">
        <TextField
          size="small"
          className="text-field-request filter-title"
          variant="outlined"
          placeholder={t('search')}
          value={searchText}
          onChange={(e) => onSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search className="search_icon" />
              </InputAdornment>
            ),
            inputProps: { 'data-id': 'input-search-in-pronunciation' },
          }}
        />
        <div ref={refClickOutSide}>
          <StyledIconButton
            height="40px"
            width="40px"
            onClick={(e) => {
              setAnchorStatusRequest(e.currentTarget);
              setOpenPopperStatusRequest((prev) => !prev);
            }}
            borderRadius="8px"
            isDisableHover
            padding="12px"
            data-id="sort-pronunciation-dict"
          >
            <Tooltip
              arrow
              title={optionsFilter[sort] || t('sort')}
              placement="top"
            >
              <div className="wrap_btn">
                <SortRoundedIcon className="icon" />
              </div>
            </Tooltip>
            <Popper
              transition
              style={{
                zIndex: 9999,
                width: '180px',
                color: '#1E164E',
              }}
              anchorEl={anchorStatusRequest}
              open={openPopperStatusRequest}
              placement="bottom-end"
            >
              {({ TransitionProps }) => (
                <Fade
                  style={{ padding: '4px 0px' }}
                  {...TransitionProps}
                  timeout={350}
                >
                  <Paper>
                    {Object.keys(optionsFilter)?.map((option) => (
                      <MenuItem
                        key="recently"
                        className="option_filter"
                        style={{
                          color: '#1E164E',
                          fontSize: '14px',
                          height: '40px',
                          boxSizing: 'border-box',
                          padding: '10px 16px 10px 16px',
                          fontWeight: 500,
                          background: option === sort && '#1E164E14',
                        }}
                        onMouseEnter={(e) => {
                          if (option === sort) return;
                          e.currentTarget.style.backgroundColor = '#1E164E14';
                        }}
                        onMouseLeave={(e) => {
                          if (option === sort) return;
                          e.currentTarget.style.backgroundColor = '';
                        }}
                        onClick={() => {
                          onSort(option);
                          setAnchorStatusRequest(null);
                          setOpenPopperStatusRequest((prev) => !prev);
                        }}
                      >
                        {optionsFilter[option]}
                      </MenuItem>
                    ))}
                  </Paper>
                </Fade>
              )}
            </Popper>
          </StyledIconButton>
        </div>
      </div>
    </div>
  );
};

const Actions = ({
  onSelectedItem,
  fetchDictionary,
  words,
  wordsData,
  selectedItem,
  onTextChangeWhenDeleteWord,
}) => {
  const { t } = useTranslation();
  const [anchorResetEl, setAnchorResetEl] = useState(null);
  const isSelectAll =
    selectedItem.length > 0 &&
    selectedItem.filter((x) => words?.some((item) => item.word === x.word))
      ?.length === words?.length;
  const onDeleteDictionary = async () => {
    await apis.voices.createDictionary([
      ...wordsData?.filter(
        (item) => !selectedItem?.some((i) => i.word === item.word),
      ),
    ]);
    onTextChangeWhenDeleteWord(selectedItem);
    onSelectedItem([]);
    await fetchDictionary(true);
    setAnchorResetEl(null);
  };

  const renderActionConfirm = () => (
    <StyledActionConfirm>
      <Button
        className="btn-delete button outlined-button"
        variant="outlined"
        onClick={onDeleteDictionary}
      >
        {t('delete')}
      </Button>
      <Button
        className="btn-cancel button"
        variant="contained"
        onClick={() => setAnchorResetEl(null)}
      >
        {t('cancel')}
      </Button>
    </StyledActionConfirm>
  );

  const refClickOutSide = useClickOutside(() => {
    setAnchorResetEl(null);
  });
  return (
    <div className="actions">
      <div className="action-checkbox">
        <IconButton
          onClick={() => onSelectedItem([])}
          className="clear-icon"
          size="small"
        >
          <ClearRoundedIcon />
        </IconButton>
        <div className="title_list_dictionary">
          {t('amountDictionarySelected', { length: selectedItem?.length })}
        </div>

        {isSelectAll ? (
          <Button
            variant="outlined"
            color="primary"
            className="detect-all-button"
            onClick={() => {
              onSelectedItem(
                selectedItem.filter(
                  (item) =>
                    !words.some((itemWord) => itemWord?.word === item.word),
                ),
              );
            }}
          >
            {t('deselectAll')}
          </Button>
        ) : (
          <Button
            variant="outlined"
            color="primary"
            type="checkox"
            className="detect-all-button"
            onClick={() => {
              const tempArray = [...selectedItem];
              (words || []).forEach((itemWord) => {
                if (
                  itemWord &&
                  !selectedItem.some((item) => item.word === itemWord?.word)
                ) {
                  tempArray.push(itemWord);
                }
              });
              onSelectedItem(tempArray);
            }}
          >
            {t('selectAll')}
          </Button>
        )}
      </div>
      <div ref={refClickOutSide}>
        <Popconfirm
          anchorRef={anchorResetEl}
          useArrow
          placement="top-start"
          onClose={() => {}}
          icon={<WarningRoundedIcon sx={{ fontSize: 20 }} color="warning" />}
          newDictionary
          content={
            selectedItem?.length === words?.length
              ? t('deleteAllDictionary')
              : t('yousurewanttoDelete')
          }
          actionComponent={renderActionConfirm()}
        />
        <Button
          variant="outlined"
          data-id="delete-pronunciation"
          color="error"
          className="delete-button"
          onClick={(e) => setAnchorResetEl(e.currentTarget)}
          startIcon={<img src={DeleteIcon} alt="delete" />}
        >
          {t('delete')}
        </Button>
      </div>
    </div>
  );
};

const ListData = ({
  loading,
  words = [],
  wordsData,
  selectedItem,
  onSelectedItem,
  fetchDictionary,
  onTextChangeWhenAddWord,
}) => {
  const onChangeCheckbox = (currentSelectItem) => {
    if (selectedItem.some((item) => item.word === currentSelectItem.word)) {
      onSelectedItem((prev) =>
        prev.filter((item) => item.word !== currentSelectItem?.word),
      );
    } else {
      onSelectedItem((prev) => prev.concat([currentSelectItem]));
    }
  };

  return (
    <div className="items">
      <VirtualList
        data={words}
        disabled={false}
        className="scrollbar"
        height={words?.length > 0 ? HEIGHT_LIST : 0}
        itemHeight={HEIGHT_ITEM}
        itemKey="idVirtualList"
      >
        {(rowData, index) => (
          <div
            key={`${rowData?.word}-${rowData?.pronunciation}`}
            className="item"
          >
            <StyledTableCell
              className="checkbox-cell checkbox-dictionary"
              padding="checkbox"
              align="left"
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                className="body-checkbox"
                checked={selectedItem.some(
                  (item) => item.word === rowData.word,
                )}
                sx={{
                  color: '#1E164E',
                  '&.Mui-checked': {
                    color: '#1E164E',
                  },
                }}
                onChange={() => onChangeCheckbox(rowData)}
              />
            </StyledTableCell>
            <InputDictionary
              type="word"
              value={rowData?.word}
              onWordRowChange={() => onTextChangeWhenAddWord(rowData)}
              wordsView={words}
              wordsData={wordsData}
              index={index}
              fetchDictionary={fetchDictionary}
            />
            <InputDictionary
              type="pronunciation"
              value={rowData?.pronunciation}
              wordsView={words}
              wordsData={wordsData}
              onWordRowChange={() => onTextChangeWhenAddWord(rowData)}
              index={index}
              fetchDictionary={fetchDictionary}
            />
          </div>
        )}
      </VirtualList>

      {!loading && !words?.length && <NoData height="264px" />}
    </div>
  );
};

const ListDictionary = ({ children }) => <div>{children}</div>;
ListDictionary.FilterDictionary = FilterDictionary;
ListDictionary.ListData = ListData;
ListDictionary.Actions = Actions;
export default ListDictionary;
